/**
 * AdapterStateManager.js
 *
 * State machine manager specifically for RendererAdapter
 * This provides the state management that RendererAdapter expects
 *
 * Features:
 * - Defined states with valid transition rules
 * - State history tracking with configurable limits
 * - Metadata tracking per state
 * - Force state capability for error recovery
 *
 * State History Behavior:
 * - History is kept to maxHistorySize (default: 100)
 * - When limit is reached, oldest 20% of entries are removed
 * - This provides a sliding window without losing too much context
 */

// Define adapter states
export const AdapterState = {
  UNINITIALIZED: 'uninitialized',
  INITIALIZING: 'initializing',
  INITIALIZED: 'initialized',
  READY: 'ready',
  RENDERING: 'rendering',
  REFRESHING: 'refreshing',
  DESTROYING: 'destroying',
  DESTROYED: 'destroyed',
  ERROR: 'error'
};

/**
 * State manager for adapter components with state machine logic
 */
export class AdapterStateManager {
  /**
   * @param {Object} logger - Optional logger instance
   * @param {Object} options - Optional configuration
   * @param {number} options.maxHistorySize - Maximum history entries (default: 100)
   */
  constructor(logger = null, options = {}) {
    this.logger = logger;
    this.currentState = AdapterState.UNINITIALIZED;
    this.stateHistory = [];
    this.metadata = {};

    // Configurable history limits
    this.maxHistorySize = options.maxHistorySize || 100;

    // Define valid state transitions (not changed per constraints)
    this.validTransitions = {
      [AdapterState.UNINITIALIZED]: [AdapterState.INITIALIZING],
      [AdapterState.INITIALIZING]: [AdapterState.INITIALIZED, AdapterState.ERROR],
      [AdapterState.INITIALIZED]: [AdapterState.READY, AdapterState.DESTROYING, AdapterState.ERROR],
      [AdapterState.READY]: [AdapterState.RENDERING, AdapterState.REFRESHING, AdapterState.DESTROYING, AdapterState.ERROR],
      [AdapterState.RENDERING]: [AdapterState.READY, AdapterState.ERROR],
      [AdapterState.REFRESHING]: [AdapterState.READY, AdapterState.ERROR],
      [AdapterState.DESTROYING]: [AdapterState.DESTROYED],
      [AdapterState.DESTROYED]: [], // Terminal state
      [AdapterState.ERROR]: [AdapterState.INITIALIZING, AdapterState.DESTROYING] // Can retry or destroy
    };
  }

  /**
   * Get current state
   */
  getCurrentState() {
    return this.currentState;
  }

  /**
   * Check if adapter is in a specific state
   */
  isInState(state) {
    return this.currentState === state;
  }

  /**
   * Check if adapter is ready for operations
   */
  isReady() {
    return this.currentState === AdapterState.READY;
  }

  /**
   * Check if adapter is operational (can perform actions)
   */
  isOperational() {
    const operationalStates = [
      AdapterState.INITIALIZED,
      AdapterState.READY,
      AdapterState.RENDERING,
      AdapterState.REFRESHING
    ];
    return operationalStates.includes(this.currentState);
  }

  /**
   * Check if transition to a state is valid
   */
  canTransitionTo(newState) {
    const allowedStates = this.validTransitions[this.currentState] || [];
    return allowedStates.includes(newState);
  }

  /**
   * Transition to a new state
   * @param {string} newState - Target state
   * @param {Object} metadata - Optional metadata for this transition
   * @throws {Error} If transition is not valid
   */
  transitionTo(newState, metadata = {}) {
    if (!this.canTransitionTo(newState)) {
      const error = `Invalid state transition: ${this.currentState} -> ${newState}`;
      if (this.logger) {
        this.logger.error(error);
      }
      throw new Error(error);
    }

    const previousState = this.currentState;
    this.currentState = newState;

    // DECISION: Merge metadata rather than replace
    // This preserves context from previous transitions while adding new info
    // If you need to replace entirely, use: this.metadata = { ...metadata };
    this.metadata = { ...this.metadata, ...metadata };

    // Add to history
    this._recordTransition(previousState, newState, metadata, false);

    if (this.logger) {
      this.logger.debug(`State transition: ${previousState} -> ${newState}`, metadata);
    }
  }

  /**
   * Force state (for error recovery)
   * Bypasses validation - use with caution
   */
  forceState(newState, metadata = {}) {
    if (this.logger) {
      this.logger.warn(`Forcing state transition: ${this.currentState} -> ${newState}`);
    }

    const previousState = this.currentState;
    this.currentState = newState;

    // For forced transitions, replace metadata entirely to clear error state
    this.metadata = { ...metadata };

    this._recordTransition(previousState, newState, metadata, true);
  }

  /**
   * Record a state transition in history
   * @private
   */
  _recordTransition(from, to, metadata, forced) {
    this.stateHistory.push({
      from,
      to,
      timestamp: Date.now(),
      metadata: { ...metadata },
      ...(forced && { forced: true })
    });

    // IMPROVED: More gradual history truncation
    // Instead of dropping from 50 to 25, we remove oldest 20% when limit reached
    // This provides a sliding window without losing too much context
    if (this.stateHistory.length > this.maxHistorySize) {
      const removeCount = Math.ceil(this.maxHistorySize * 0.2); // Remove 20%
      this.stateHistory = this.stateHistory.slice(removeCount);
    }
  }

  /**
   * Get state history
   * @returns {Array} Copy of state history
   */
  getStateHistory() {
    return [...this.stateHistory];
  }

  /**
   * Get current metadata
   * @returns {Object} Copy of current metadata
   */
  getMetadata() {
    return { ...this.metadata };
  }

  /**
   * Set metadata directly (without state transition)
   * Useful for updating context without changing state
   * @param {Object} metadata - Metadata to merge
   * @param {boolean} replace - If true, replace entirely instead of merge
   */
  setMetadata(metadata, replace = false) {
    if (replace) {
      this.metadata = { ...metadata };
    } else {
      this.metadata = { ...this.metadata, ...metadata };
    }
  }

  /**
   * Reset state manager to initial state
   */
  reset() {
    this.currentState = AdapterState.UNINITIALIZED;
    this.stateHistory = [];
    this.metadata = {};

    if (this.logger) {
      this.logger.debug('State manager reset');
    }
  }

  /**
   * Get status information
   */
  getStatus() {
    return {
      currentState: this.currentState,
      isReady: this.isReady(),
      isOperational: this.isOperational(),
      metadata: this.getMetadata(),
      historyLength: this.stateHistory.length,
      maxHistorySize: this.maxHistorySize,
      lastTransition: this.stateHistory[this.stateHistory.length - 1] || null
    };
  }
}

// For backward compatibility, export as StateManager too
export const StateManager = AdapterStateManager;
